import * as Dialog from "@radix-ui/react-dialog";
import { FileText, FlowArrow, MagnifyingGlass, Scales, VideoCamera, X } from "@phosphor-icons/react";
import { useDeferredValue, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../context/WorkspaceContext";
import type { SearchKind } from "../types/domain";

const kindMeta: Record<SearchKind, { labelKey: string; icon: typeof FileText }> = {
  card: { labelKey: "search.cards", icon: FileText },
  video: { labelKey: "search.videos", icon: VideoCamera },
  workflow: { labelKey: "search.workflows", icon: FlowArrow },
  legal: { labelKey: "search.legal", icon: Scales },
};

export function GlobalSearchDialog({ open, onOpenChange }: { open: boolean; onOpenChange(open: boolean): void }) {
  const { t } = useTranslation();
  const { search } = useWorkspace();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const deferredQuery = useDeferredValue(query);
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => search(deferredQuery), [deferredQuery, search]);
  const groups = useMemo(() => Object.entries(kindMeta).map(([kind, meta]) => ({
    kind: kind as SearchKind,
    ...meta,
    items: results.filter((result) => result.kind === kind),
  })).filter((group) => group.items.length), [results]);
  // Keyboard order has to follow the rendered groups, not the raw score order.
  const ordered = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const activeResult = ordered[Math.min(activeIndex, ordered.length - 1)];

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 50);
    else setQuery("");
  }, [open]);
  useEffect(() => setActiveIndex(0), [deferredQuery]);

  const openResult = (route: string) => {
    onOpenChange(false);
    navigate(route);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!ordered.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (Math.min(current, ordered.length - 1) + 1) % ordered.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (Math.min(current, ordered.length - 1) + ordered.length - 1) % ordered.length);
    } else if (event.key === "Enter" && activeResult) {
      event.preventDefault();
      openResult(activeResult.route);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="search-dialog" aria-describedby={undefined}>
          <Dialog.Title className="sr-only">{t("search.label")}</Dialog.Title>
          <div className="search-input-row">
            <MagnifyingGlass size={24} />
            <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={onKeyDown} placeholder={t("search.placeholder")} />
            <Dialog.Close className="icon-button" aria-label={t("common.close")}><X size={20} /></Dialog.Close>
          </div>
          <div className="search-results" aria-live="polite">
            {deferredQuery.length < 2 && <p className="search-hint">{t("search.hint")}</p>}
            {deferredQuery.length >= 2 && !results.length && <p className="search-hint">{t("search.empty")}</p>}
            {groups.map((group) => {
              const Icon = group.icon;
              return <section className="search-group" key={group.kind}>
                <h3><Icon size={17} /> {t(group.labelKey)}<span>{group.items.length}</span></h3>
                {group.items.map((result) => <button
                  key={`${result.kind}-${result.id}`}
                  className={`search-result ${result === activeResult ? "is-active" : ""}`}
                  ref={result === activeResult ? (node) => node?.scrollIntoView({ block: "nearest" }) : undefined}
                  onMouseEnter={() => setActiveIndex(ordered.indexOf(result))}
                  onClick={() => openResult(result.route)}
                >
                  <span className="search-result-copy"><strong>{highlight(result.title, deferredQuery)}</strong><small>{result.excerpt}</small></span>
                  <span className="search-location">{result.location}</span>
                </button>)}
              </section>;
            })}
          </div>
          <footer className="search-footer"><span>↑↓ {t("search.navigation")}</span><span>Enter {t("search.open")}</span><span>Esc {t("search.close")}</span></footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function highlight(text: string, query: string) {
  const index = text.toLocaleLowerCase("bg").indexOf(query.toLocaleLowerCase("bg"));
  if (index < 0) return text;
  return <>{text.slice(0, index)}<mark>{text.slice(index, index + query.length)}</mark>{text.slice(index + query.length)}</>;
}
